/**
 * Who this browser says it is — and what the server does with that.
 *
 * Two modes, and the difference is the whole security story:
 *
 * **Deployed.** A Cognito hosted-UI sign-in returns an id token. It travels as
 * `Authorization: Bearer`, the API verifies its signature against the pool's JWKS, and the
 * identity comes out of the verified claims. Nothing this file sends can change who the server
 * thinks you are.
 *
 * **Local.** `X-Dev-Persona: <org>/<email>` names a seeded person with no proof at all. The API
 * refuses it unless `IDENTITY_MODE=dev_header`, which fails closed everywhere else. It exists so
 * the screens can be built before Cognito pools are.
 *
 * `X-Organization` names the tenant in both modes and is never trusted on its own: the server
 * re-resolves membership, role and permissions from the database on every request. A browser
 * that lies about its organization gets someone else's 403, not someone else's diary.
 */
export interface Identity {
  organizationSlug: string
  /** Set in local mode only. */
  persona?: string
  /** Set when signed in through Cognito. */
  idToken?: string
}

const STORAGE_KEY = 'haai.identity'

export function loadIdentity(): Identity | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Identity) : null
  } catch {
    // Private browsing, blocked storage, corrupt value. Signing in again is cheap.
    return null
  }
}

export function saveIdentity(identity: Identity | null): void {
  try {
    if (identity) localStorage.setItem(STORAGE_KEY, JSON.stringify(identity))
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Not being able to remember the session is a worse experience, not a broken one.
  }
}

export function headersFor(identity: Identity): HeadersInit {
  const headers: Record<string, string> = { 'X-Organization': identity.organizationSlug }
  if (identity.idToken) headers.Authorization = `Bearer ${identity.idToken}`
  else if (identity.persona) headers['X-Dev-Persona'] = identity.persona
  return headers
}

const VERIFIER_KEY = 'haai.pkce.verifier'

/** Which pool a sign-in goes to. Staff are invited by the shop; customers sign themselves up. */
export type Audience = 'staff' | 'customer'

const POOL_KEY = 'haai.pool'

function pool(audience: Audience): { domain: string; clientId: string } | undefined {
  const domain =
    audience === 'customer'
      ? import.meta.env.VITE_CUSTOMER_COGNITO_DOMAIN
      : import.meta.env.VITE_COGNITO_DOMAIN
  const clientId =
    audience === 'customer'
      ? import.meta.env.VITE_CUSTOMER_COGNITO_CLIENT_ID
      : import.meta.env.VITE_COGNITO_CLIENT_ID
  return domain && clientId ? { domain, clientId } : undefined
}

/** The pool the sign-in in flight belongs to, remembered across the redirect. */
function cognito(): { domain: string; clientId: string } | undefined {
  const remembered = sessionStorage.getItem(POOL_KEY)
  return pool(remembered === 'customer' ? 'customer' : 'staff')
}

/** Both pools, for a screen that offers a choice. */
export function availableAudiences(): Audience[] {
  return (['staff', 'customer'] as Audience[]).filter((a) => pool(a) !== undefined)
}

/** True once pools are configured. Local development has none, and says so by their absence. */
export function isHostedSignInConfigured(): boolean {
  return availableAudiences().length > 0
}

function redirectUri(): string {
  return window.location.origin + '/'
}

function base64url(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * Begin an authorization-code sign-in with PKCE.
 *
 * Not the implicit flow, which this used to be. Implicit returns the token in the URL
 * fragment, so it lands in browser history, in screenshots and in anything that reads a
 * referrer — and it cannot issue a refresh token, which forces the choice between a long-lived
 * token sitting in the address bar and signing in again every hour. OAuth 2.1 and the
 * browser-apps BCP both drop it, and the Cognito app client does not enable it.
 *
 * PKCE is what replaces the client secret a single-page application cannot keep: the verifier
 * never leaves this browser, only its SHA-256 does, so an intercepted authorization code is
 * useless to whoever intercepted it.
 */
export async function beginSignIn(audience: Audience = 'staff'): Promise<void> {
  const config = pool(audience)
  if (!config) {
    throw new Error(`No Cognito pool is configured for ${audience} in this build.`)
  }
  // Remembered so the redirect back exchanges the code against the pool that issued it.
  // Getting this wrong is an `invalid_grant` that reads like a bad password.
  sessionStorage.setItem(POOL_KEY, audience)

  if (!window.crypto?.subtle) {
    // Only available in a secure context. A page served over plain HTTP reaches this line.
    throw new Error(
      'This browser exposes no Web Crypto, so the PKCE challenge cannot be generated. That ' +
        'usually means the page is not being served over HTTPS.',
    )
  }

  const verifier = base64url(crypto.getRandomValues(new Uint8Array(64)).buffer)
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  // Session storage, not local: a verifier outliving the tab it belongs to is a loose end,
  // and this one is only needed between the redirect out and the redirect back.
  sessionStorage.setItem(VERIFIER_KEY, verifier)

  const query = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: 'openid email',
    redirect_uri: redirectUri(),
    code_challenge: base64url(digest),
    code_challenge_method: 'S256',
  })
  window.location.assign(`${config.domain}/oauth2/authorize?${query}`)
}

/**
 * What a page load turned out to be: not a sign-in at all, a completed one, or a failed one.
 *
 * This used to be `Promise<string | undefined>`, and `undefined` meant five different things:
 * not a callback, no pool configured, a redirect that started elsewhere, a refused token
 * exchange, and a response with no id_token in it. Every one of them put the user back on the
 * sign-in screen with nothing said, which is the worst possible behaviour for the one flow a
 * user cannot inspect for themselves.
 *
 * The case that matters most is the one that was missing entirely: an OAuth provider reports
 * failure by redirecting back with `?error=` and no `code`. The old code checked for `code`,
 * found none, and returned `undefined` — so a `redirect_mismatch`, an unauthorised client or a
 * revoked user looked exactly like never having clicked the button.
 */
export type SignInOutcome =
  | { state: 'none' }
  | { state: 'signed-in'; idToken: string }
  | { state: 'failed'; reason: string }

/**
 * Finish the sign-in if this page load is the redirect back.
 *
 * The authorization code is single-use and short-lived, but it is still in the address bar, so
 * it is stripped as soon as it has been exchanged — successfully or not.
 */
export async function completeSignIn(): Promise<SignInOutcome> {
  const config = cognito()
  const query = new URLSearchParams(window.location.search)
  const code = query.get('code')
  const error = query.get('error')

  if (!config) return { state: 'none' }

  // The provider said no. This is a redirect back like any other; the only difference is which
  // parameters it carries, and reading only `code` is how it went unnoticed.
  if (error) {
    window.history.replaceState({}, '', window.location.pathname)
    const description = query.get('error_description')
    return {
      state: 'failed',
      reason: description ? `${error}: ${description}` : error,
    }
  }

  if (!code) return { state: 'none' }

  const verifier = sessionStorage.getItem(VERIFIER_KEY)
  sessionStorage.removeItem(VERIFIER_KEY)
  window.history.replaceState({}, '', window.location.pathname)
  if (!verifier) {
    return {
      state: 'failed',
      reason:
        'This sign-in started in a different tab or session, so the PKCE verifier that proves ' +
        'it was not intercepted is gone. Start again from this tab.',
    }
  }

  let response: Response
  try {
    response = await fetch(`${config.domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.clientId,
        code,
        redirect_uri: redirectUri(),
        code_verifier: verifier,
      }),
    })
  } catch {
    // A CORS refusal reaches the page as a network error with no detail, by design.
    return {
      state: 'failed',
      reason: `Could not reach ${config.domain} to exchange the code. If the browser console ` +
        'shows a CORS error, this origin is not one the pool allows.',
    }
  }

  if (!response.ok) {
    // Cognito answers with `{"error": "..."}`; anything else is reported as the status.
    const detail = await response
      .json()
      .then((body: { error?: string; error_description?: string }) =>
        body.error_description ?? body.error ?? '',
      )
      .catch(() => '')
    return {
      state: 'failed',
      reason: detail
        ? `The token exchange was refused: ${detail}`
        : `The token exchange answered ${response.status}.`,
    }
  }

  const payload = (await response.json()) as { id_token?: string }
  if (!payload.id_token) {
    return {
      state: 'failed',
      reason: 'The token exchange succeeded but returned no id_token, so there is nothing to ' +
        'identify this browser with. Check the app client has the `openid` scope.',
    }
  }
  return { state: 'signed-in', idToken: payload.id_token }
}

/**
 * `?persona=<org>/<email>` — a local-development convenience, and nothing more.
 *
 * It lets you open the app straight as a given seeded person, which is how the screens get
 * checked without clicking through a sign-in each time. It is refused in two independent
 * places: here, if a Cognito pool is configured at all, and again by the API, which rejects
 * `X-Dev-Persona` unless `IDENTITY_MODE=dev_header`. Neither is set on a deployed server, so
 * a link like this against production is just a query string nobody reads.
 */
export function personaFromQuery(): Identity | undefined {
  if (isHostedSignInConfigured()) return undefined
  const persona = new URLSearchParams(window.location.search).get('persona')
  if (!persona || !persona.includes('/')) return undefined
  const [slug] = persona.split('/')
  return { organizationSlug: slug ?? '', persona }
}
